import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowLeft, Sparkles, Loader2, Check, Coins, Square, Camera, LayoutList, Mic } from 'lucide-react';
import { useGraphStore, nextNodeId } from '../store/graphStore';
import { nodeTypes } from '../components/studio/nodes';
import LeftPanel from '../components/studio/LeftPanel';
import RightPanel from '../components/studio/RightPanel';
import ResultsStrip from '../components/studio/ResultsStrip';
import SimpleMode from '../components/studio/SimpleMode';
import VideoDialog from '../components/studio/VideoDialog';
import AvatarVideoDialog from '../components/studio/AvatarVideoDialog';
import PhotoRecipes from '../components/PhotoRecipes';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

function Canvas({ workflowId }) {
  const navigate = useNavigate();
  const { user, updateCredits } = useAuth();
  const { screenToFlowPosition } = useReactFlow();
  const wrapper = useRef(null);
  const {
    nodes, edges, onNodesChange, onEdgesChange, onConnect, addNode,
    updateNodeData, removeNode, selectedId, setSelected, setGraph, getGraphJson, dirty, markClean,
  } = useGraphStore();

  const [assets, setAssets] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [wf, setWf] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generations, setGenerations] = useState([]);
  const [compiled, setCompiled] = useState(null);
  const [compiling, setCompiling] = useState(false);
  const [videoSource, setVideoSource] = useState(null);
  // Off until a video model is both reliable and cheap. Withholding the handler
  // is what hides the button, so there is one switch rather than three.
  const [videoOn, setVideoOn] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [sessions, setSessions] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarProgress, setAvatarProgress] = useState(null);

  // 'simple' | 'canvas'. Una sesion que viene de una receta abre en Simple
  // porque todo esta ya decidido; un lienzo en blanco abre en Canvas. La
  // eleccion se recuerda por sesion.
  const modeKey = `idolaris_studio_mode_${workflowId}`;
  const [mode, setMode] = useState(null);
  const switchMode = (m) => {
    setMode(m);
    try { localStorage.setItem(modeKey, m); } catch { /* private mode */ }
  };

  // Settings a template or a starter left on the video node, handed to the
  // dialog so it opens on this workflow's brief instead of a generic default.
  // Only when there is exactly one: with two, guessing which one the creator
  // meant would fill the box with the wrong script.
  const videoSeed = useMemo(() => {
    const vids = nodes.filter((n) => n.type === 'generator.video');
    return vids.length === 1 ? vids[0].data : null;
  }, [nodes]);
  const saveTimer = useRef(null);
  const compileTimer = useRef(null);
  const autogenDone = useRef(false);
  const cancelRef = useRef(false);
  // Sync guards the async setState cannot give: block the double-click that
  // fires twice before `generating` re-renders, and count the frames of a
  // batch so the button only comes back when the LAST one lands (it used to
  // re-enable on the first, inviting a second, fully-charged batch).
  const generatingRef = useRef(false);
  const pendingFrames = useRef(0);
  const frameDone = () => {
    pendingFrames.current = Math.max(0, pendingFrames.current - 1);
    if (pendingFrames.current === 0) {
      generatingRef.current = false;
      setGenerating(false);
    }
  };

  // Initial load
  useEffect(() => {
    api.publicConfig()
      .then((c) => setVideoOn(!!c.video_enabled))
      .catch(() => { /* stays off, which is the safe direction */ });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [w, a, t, g, inf] = await Promise.all([
          api.getWorkflow(workflowId), api.listAssets(), api.getStyleTemplates(), api.listGenerations(workflowId), api.listInfluencers(),
        ]);
        setWf(w);
        setGraph(w.graph_json || { nodes: [], edges: [] });
        // Modo por defecto: Simple si la sesion viene de una receta, Canvas si
        // no. Lo que el usuario haya elegido antes para ESTA sesion gana.
        // Los UGC de video tambien traen template_slug, pero SimpleMode
        // conduce el generador de imagen. Un grafo sin ese nodo no tiene nada
        // que conducir, y uno CON nodo de video esconderia el clip (el motivo
        // de la sesion) detras de un enlace: ambos abren en canvas.
        const gNodes = (w.graph_json?.nodes) || [];
        const hasImageGen = gNodes.some((n) => n.type === 'generator.image');
        const hasVideoGen = gNodes.some((n) => n.type === 'generator.video' || n.type === 'generator.avatar_video');
        let saved = null;
        try { saved = localStorage.getItem(`idolaris_studio_mode_${workflowId}`); } catch { /* private mode */ }
        const wanted = saved === 'simple' || saved === 'canvas'
          ? saved : ((w.recipe_label || w.template_slug) ? 'simple' : 'canvas');
        setMode(wanted === 'simple' && (!hasImageGen || hasVideoGen) ? 'canvas' : wanted);
        setAssets(a);
        setTemplates(t);
        setGenerations(g);
        setInfluencers(inf || []);
      } catch (e) {
        toast({ title: 'Could not load the session', description: e.message });
      }
    })();
    // eslint-disable-next-line
  }, [workflowId]);

  const reloadAssets = useCallback(async () => {
    try { setAssets(await api.listAssets()); } catch (e) { /* noop */ }
  }, []);

  // Autosave (debounced)
  useEffect(() => {
    if (!dirty || !wf) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.updateWorkflow(workflowId, { graph_json: getGraphJson() });
        markClean();
      } catch (e) { /* keep dirty */ }
      setSaving(false);
    }, 1500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
    // eslint-disable-next-line
  }, [nodes, edges, dirty]);

  // Live compile when a generator is selected
  const selectedNode = nodes.find((n) => n.id === selectedId) || null;
  const selKind = selectedNode?.data?.kind;

  // Resolve the influencer upstream of the selected video node so its saved voice powers the TTS.
  const resolveInfluencer = (videoNode) => {
    if (!videoNode || videoNode.data?.kind !== 'video') return null;
    const walk = (nodeId, depth) => {
      if (depth > 4) return null;
      for (const e of edges.filter((ed) => ed.target === nodeId)) {
        const src = nodes.find((n) => n.id === e.source);
        if (!src) continue;
        if (src.type === 'asset.influencer') return src;
        const up = walk(src.id, depth + 1);
        if (up) return up;
      }
      return null;
    };
    const infNode = walk(videoNode.id, 0);
    if (!infNode) return null;
    const full = influencers.find((i) => i.id === infNode.data?.influencerId);
    return full || { id: infNode.data?.influencerId, name: infNode.data?.name, persona: infNode.data?.persona, voice: 'nova', language: 'English' };
  };
  const selectedInfluencer = resolveInfluencer(selectedNode);
  // Shots times the tier, read off whichever generator the button will use.
  const genCost = (() => {
    const g = (selectedNode?.data?.kind === 'generator' ? selectedNode : null)
      || nodes.find((n) => n.type === 'generator.image');
    const unit = compiled?.estimatedCredits;
    if (!g || !unit) return null;
    const shots = Math.max(1, Math.min(Number(g.data?.shots) || 1, 12));
    return unit * shots;
  })();
  useEffect(() => {
    if (selKind !== 'generator') { setCompiled(null); return; }
    if (compileTimer.current) clearTimeout(compileTimer.current);
    compileTimer.current = setTimeout(async () => {
      setCompiling(true);
      try {
        const res = await api.compile({ graph_json: getGraphJson(), targetNodeId: selectedNode.id });
        setCompiled(res);
      } catch (e) {
        setCompiled({ error: e.message });
      }
      setCompiling(false);
    }, 450);
    return () => compileTimer.current && clearTimeout(compileTimer.current);
    // eslint-disable-next-line
  }, [nodes, edges, selectedId, selKind]);

  // Which node, if any, sits under the pointer, and what type it is. The
  // element is asked rather than the positions being compared, so the browser
  // does the hit testing and zoom and pan come for free.
  const nodeUnder = (x, y) => {
    const el = document.elementFromPoint(x, y)?.closest('.react-flow__node');
    if (!el) return null;
    const cls = [...el.classList].find((c) => c.startsWith('react-flow__node-'));
    return { el, id: el.getAttribute('data-id'),
             type: cls ? cls.slice('react-flow__node-'.length) : null };
  };

  const hovered = useRef(null);
  const clearHover = () => {
    if (hovered.current) {
      hovered.current.style.outline = '';
      hovered.current.style.outlineOffset = '';
      hovered.current = null;
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    clearHover();
    const raw = e.dataTransfer.getData('application/idolaris');
    if (!raw) return;
    const payload = JSON.parse(raw);

    // Dropped onto a node of the same kind: fill that one in instead of leaving
    // a second subject floating next to it. This is how a template's described
    // stand-in becomes the creator's actual photo without rebuilding anything
    // around it, so the scene, the styling and the wiring all survive.
    const over = nodeUnder(e.clientX, e.clientY);
    if (over?.id && over.type === payload.type
        && (payload.group === 'asset' || payload.group === 'influencer')) {
      updateNodeData(over.id, { ...payload.data });
      setSelected(over.id);
      // A generator whose prompt was written by hand will not notice the swap:
      // its words are frozen and only the reference image changes. Better said
      // here than discovered in the render.
      const frozen = nodes.some((n) => n.type === 'generator.image'
        && typeof n.data?.promptOverride === 'string' && n.data.promptOverride.trim());
      toast({
        title: 'Swapped in',
        description: frozen
          ? `${payload.data.name} replaces what was in that node, but this canvas has a hand written prompt, so the words will not change. Open the generator and choose "Use the canvas version".`
          : `${payload.data.name} replaces what was in that node. The rest of the canvas is untouched.`,
      });
      return;
    }

    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const id = nextNodeId(payload.group);
    addNode({ id, type: payload.type, position, data: { ...payload.data } });
    setSelected(id);
  }, [screenToFlowPosition, addNode, setSelected, updateNodeData]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const over = nodeUnder(e.clientX, e.clientY);
    // types is readable mid-drag where the payload is not, which is why the
    // node type was smuggled in as a format name on the way out.
    const fits = over?.type && e.dataTransfer.types.includes(`idolaris/${over.type}`);
    if (!fits) { clearHover(); return; }
    if (hovered.current !== over.el) {
      clearHover();
      over.el.style.outline = '2px dashed rgba(255,255,255,0.55)';
      over.el.style.outlineOffset = '4px';
      hovered.current = over.el;
    }
  }, []);

  const onDragLeave = useCallback(() => clearHover(), []);

  // Click-to-add: drop the node in the centre of the current viewport (parity with drag).
  const addNodeAtCenter = useCallback((payload) => {
    const rect = wrapper.current?.getBoundingClientRect();
    const cx = rect ? rect.left + rect.width / 2 : 500;
    const cy = rect ? rect.top + rect.height / 2 : 320;
    const jitter = () => (Math.random() - 0.5) * 60;
    const position = screenToFlowPosition({ x: cx + jitter(), y: cy + jitter() });
    const id = nextNodeId(payload.group);
    addNode({ id, type: payload.type, position, data: { ...payload.data } });
    setSelected(id);
  }, [screenToFlowPosition, addNode, setSelected]);

  const pollGeneration = useCallback((genId, nodeId) => {
    let tries = 0;
    const tick = async () => {
      if (cancelRef.current) return;
      tries += 1;
      try {
        const g = await api.getGeneration(genId);
        // A heartbeat from the provider itself while it works (currently
        // only the self-hosted Modal ones send one), not a simulated timer --
        // real step counts, so the node can show real progress instead of
        // just a spinner for however long the render takes.
        if (nodeId && g.progress_total) {
          updateNodeData(nodeId, { progressStep: g.progress_step, progressTotal: g.progress_total, progressStage: g.progress_stage });
        }
        if (g.status === 'succeeded') {
          if (nodeId) updateNodeData(nodeId, { status: 'succeeded', resultUrl: g.url, resultGenerationId: g.id });
          setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
          frameDone();
          const c = await api.getCredits(); updateCredits(c.balance);
          markClean();
          return;
        }
        if (g.status === 'failed') {
          if (nodeId) updateNodeData(nodeId, { status: 'failed' });
          // The log reads this list, and a failure is the entry it exists for.
          // Only the successes were being added, so the panel could show what
          // worked and never what did not.
          setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
          frameDone();
          toast({ title: 'Generation failed', description: g.error_message || 'Provider error. Credits refunded.' });
          const c = await api.getCredits(); updateCredits(c.balance);
          return;
        }
      } catch (e) { /* keep polling */ }
      // A cold provider worker (e.g. RunPod spinning up from zero) can take
      // several minutes before the job even starts, so this has to outlast
      // that, not just a warm render. Giving up silently left the UI looking
      // frozen while the job kept running server-side with nobody watching.
      if (tries < 300) setTimeout(tick, 2000);
      else {
        frameDone();
        if (nodeId) updateNodeData(nodeId, { status: 'idle' });
        toast({ title: 'Still working on the server',
                description: 'Taking longer than expected -- check Creations shortly, it may still finish.' });
      }
    };
    setTimeout(tick, 1500);
  }, [updateNodeData, updateCredits, markClean]);

  const pollVideoNode = useCallback((gid, nodeId) => {
    let tries = 0;
    const tick = async () => {
      if (cancelRef.current) return;
      tries += 1;
      try {
        const g = await api.getGeneration(gid);
        if (g.status === 'succeeded') {
          updateNodeData(nodeId, { status: 'succeeded', resultVideoUrl: g.video_url, resultVideoGenerationId: g.id });
          setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
          setGenerating(false);
          const c = await api.getCredits(); updateCredits(c.balance);
          if (g.mocked) toast({ title: 'Mock video created', description: 'Add FAL_KEY balance for real motion.' });
          else toast({ title: 'UGC video ready' });
          return;
        }
        if (g.status === 'failed') {
          updateNodeData(nodeId, { status: 'failed' });
          setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
          setGenerating(false);
          toast({ title: 'Video failed', description: g.error_message || 'Provider error. Credits refunded.' });
          const c = await api.getCredits(); updateCredits(c.balance);
          return;
        }
      } catch (e) { /* keep polling */ }
      if (tries < 120) setTimeout(tick, 3000);
      else { setGenerating(false); updateNodeData(nodeId, { status: 'idle' }); }
    };
    setTimeout(tick, 2000);
  }, [updateNodeData, updateCredits]);

  // Generate a seed image from an influencer so a Video node can source it directly.
  const seedFromInfluencer = (infNode, styleNode) => new Promise((resolve, reject) => {
    const genNodeId = `seedgen_${infNode.id}`;
    const gNodes = [
      { id: infNode.id, type: 'asset.influencer', position: { x: 0, y: 0 }, data: infNode.data },
      { id: genNodeId, type: 'generator.image', position: { x: 200, y: 0 }, data: { kind: 'generator', quality: 'premium', status: 'idle' } },
    ];
    const gEdges = [{ id: 'se', source: infNode.id, target: genNodeId, animated: true }];
    if (styleNode) { gNodes.push({ ...styleNode }); gEdges.push({ id: 'sse', source: styleNode.id, target: genNodeId, animated: true }); }
    const graph = { version: 1, nodes: gNodes, edges: gEdges };
    const idem = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
    (async () => {
      try {
        const res = await api.generate({ workflowId, targetNodeId: genNodeId, graph_json: graph, idempotencyKey: idem });
        if (res.status === 'succeeded' && res.cacheHit) { resolve(res.generationIds[0]); return; }
        let tries = 0;
        const tick = async () => {
          if (cancelRef.current) { reject(new Error('cancelled')); return; }
          tries += 1;
          try {
            const g = await api.getGeneration(res.generationIds[0]);
            if (g.status === 'succeeded') { resolve(g.id); return; }
            if (g.status === 'failed') { reject(new Error(g.error_message || 'Seed image failed')); return; }
          } catch (e) { /* keep polling */ }
          if (tries < 40) setTimeout(tick, 2000); else reject(new Error('Timed out generating the seed image'));
        };
        setTimeout(tick, 1500);
      } catch (e) { reject(e); }
    })();
  });

  // Run the session's own Image Generator and resolve with its generation id.
  // The chained path for Video: the person wired product/influencer/style into
  // the photo already — making them press Generate twice was pure ceremony.
  const runImageNodeForVideo = (genNode) => new Promise((resolve, reject) => {
    const idem = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
    (async () => {
      try {
        await api.updateWorkflow(workflowId, { graph_json: getGraphJson() });
        updateNodeData(genNode.id, { status: 'queued', resultUrl: null });
        const res = await api.generate({ workflowId, targetNodeId: genNode.id, graph_json: getGraphJson(), idempotencyKey: idem });
        const finish = async (gid) => {
          const g = await api.getGeneration(gid);
          updateNodeData(genNode.id, { status: 'succeeded', resultUrl: g.url, resultGenerationId: g.id });
          setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
          resolve(g.id);
        };
        if (res.status === 'succeeded' && res.cacheHit) { await finish(res.generationIds[0]); return; }
        let tries = 0;
        const tick = async () => {
          if (cancelRef.current) { reject(new Error('cancelled')); return; }
          tries += 1;
          try {
            const g = await api.getGeneration(res.generationIds[0]);
            if (g.status === 'succeeded') { await finish(g.id); return; }
            if (g.status === 'failed') {
              updateNodeData(genNode.id, { status: 'failed' });
              reject(new Error(g.error_message || 'The photo failed'));
              return;
            }
          } catch (e) { /* keep polling */ }
          if (tries < 150) setTimeout(tick, 2000);
          else reject(new Error('Timed out generating the photo'));
        };
        setTimeout(tick, 1500);
      } catch (e) { reject(e); }
    })();
  });

  const runVideoNode = async (videoNode) => {
    const inEdge = edges.find((e) => e.target === videoNode.id);
    const srcNode = inEdge ? nodes.find((n) => n.id === inEdge.source) : null;
    if (!srcNode || !['generator.image', 'asset.influencer'].includes(srcNode.type)) {
      toast({ title: 'Connect a source', description: 'Wire an Image Generator or an Influencer into the Video node.' });
      return;
    }
    cancelRef.current = false;
    setGenerating(true);
    updateNodeData(videoNode.id, { status: 'queued', resultVideoUrl: null });
    try {
      await api.updateWorkflow(workflowId, { graph_json: getGraphJson() });
      let genId = null;
      if (srcNode.type === 'generator.image') {
        genId = srcNode.data?.resultGenerationId;
        if (!genId && ['queued', 'running'].includes(srcNode.data?.status)) {
          // The photo is already rendering (e.g. Generate pressed moments
          // ago): wait for it instead of firing a duplicate batch.
          updateNodeData(videoNode.id, { status: 'running' });
          toast({ title: 'Waiting for the photo', description: 'The clip starts as soon as it lands.' });
          genId = await new Promise((resolve, reject) => {
            let waited = 0;
            const look = () => {
              if (cancelRef.current) { reject(new Error('cancelled')); return; }
              const fresh = useGraphStore.getState().nodes.find((n) => n.id === srcNode.id);
              if (fresh?.data?.resultGenerationId) { resolve(fresh.data.resultGenerationId); return; }
              if (fresh?.data?.status === 'failed') { reject(new Error('The photo failed')); return; }
              if ((waited += 1) < 150) setTimeout(look, 2000);
              else reject(new Error('Timed out waiting for the photo'));
            };
            look();
          });
        } else if (!genId) {
          // No photo yet: run the wired-up generator first, then the clip.
          // One press, two renders — the graph already says what to do.
          updateNodeData(videoNode.id, { status: 'running' });
          toast({ title: 'Photo first, then the clip', description: 'Generating the photo this video starts from…' });
          genId = await runImageNodeForVideo(srcNode);
          const c = await api.getCredits(); updateCredits(c.balance);
          toast({ title: 'Photo ready', description: 'Now rendering the video from it.' });
        }
      } else {
        // Influencer → Video directly: render a seed frame keeping the same face
        updateNodeData(videoNode.id, { status: 'running' });
        toast({ title: 'Preparing your influencer…', description: 'Rendering a seed image so the same face appears in the video.' });
        const styleEdge = edges.find((e) => e.target === videoNode.id && nodes.find((n) => n.id === e.source && n.type === 'asset.style'));
        const styleNode = styleEdge ? nodes.find((n) => n.id === styleEdge.source) : null;
        genId = await seedFromInfluencer(srcNode, styleNode);
        const c = await api.getCredits(); updateCredits(c.balance);
      }
      const d = videoNode.data || {};
      const res = await api.generateVideo({ generationId: genId, workflowId, model: d.model || 'fast', duration: d.duration || 8, aspect: d.aspect || '9:16', prompt: d.prompt || '', revealImageDataUrl: d.revealImageDataUrl || null });
      pollVideoNode(res.generationId, videoNode.id);
    } catch (e) {
      setGenerating(false);
      updateNodeData(videoNode.id, { status: 'idle' });
      if (e.status === 402) toast({ title: 'Not enough credits', description: `Video needs ${e.detail?.needed} credits.` });
      else if (e.message !== 'cancelled') toast({ title: 'Cannot create video', description: e.message });
    }
  };

  const handleStop = () => {
    cancelRef.current = true;
    generatingRef.current = false;
    pendingFrames.current = 0;
    setGenerating(false);
    setVideoBusy(false);
    nodes.forEach((n) => {
      if (n.data?.status === 'queued' || n.data?.status === 'running') updateNodeData(n.id, { status: 'idle' });
    });
    // Honest about what stopping does: there is no server-side cancel. A job
    // already running finishes and, if it succeeds, stays charged.
    toast({ title: 'Stopped watching', description: 'Jobs already running finish on the server: what succeeds lands in Creations (and is charged); what fails is refunded.' });
  };

  const handleGenerate = async () => {
    if (generatingRef.current) return; // double-click lands before the re-render
    if (selectedNode?.data?.kind === 'video') { runVideoNode(selectedNode); return; }
    const gen = (selectedNode?.data?.kind === 'generator' ? selectedNode : null) || nodes.find((n) => n.type === 'generator.image');
    if (!gen) { toast({ title: 'No generator', description: 'Drag an Image Generator onto the canvas first.' }); return; }
    cancelRef.current = false;
    generatingRef.current = true;
    pendingFrames.current = 0;
    setGenerating(true);
    updateNodeData(gen.id, { status: 'queued', resultUrl: null });
    try {
      await api.updateWorkflow(workflowId, { graph_json: getGraphJson() });
      const idem = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
      // A recipe arrives knowing it is a shoot rather than a photograph, and
      // carries how many frames on the generator node. One is still one.
      const shots = Math.max(1, Math.min(Number(gen.data?.shots) || 1, 12));
      const res = await api.generate({ workflowId, targetNodeId: gen.id, graph_json: getGraphJson(), idempotencyKey: idem, count: shots });
      if (res.status === 'succeeded' && res.cacheHit) {
        const g = await api.getGeneration(res.generationIds[0]);
        updateNodeData(gen.id, { status: 'succeeded', resultUrl: g.url, resultGenerationId: g.id });
        setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
        generatingRef.current = false;
        setGenerating(false);
        toast({ title: 'Served from cache', description: 'Identical graph, no credits charged.' });
        return;
      }
      // The node shows the first frame; the rest of the session lands in the
      // strip below and in the log, which is where a contact sheet is read.
      pendingFrames.current = (res.generationIds || []).length || 1;
      (res.generationIds || []).slice(1).forEach((id) => pollGeneration(id, null));
      pollGeneration(res.generationIds[0], gen.id);
      if ((res.count || 1) > 1) {
        toast({ title: `Shooting ${res.count} frames`, description: 'They appear below as each one lands.' });
      }
    } catch (e) {
      generatingRef.current = false;
      setGenerating(false);
      updateNodeData(gen.id, { status: 'idle' });
      if (e.status === 402) {
        toast({ title: 'Not enough credits', description: `You need ${e.detail?.needed} credits.` });
      } else {
        toast({ title: 'Cannot generate', description: e.message });
      }
    }
  };

  // Guided Create: auto-run the first image generation when arriving with ?autogen=1
  useEffect(() => {
    if (autogenDone.current) return;
    if (!wf || nodes.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('autogen') !== '1') return;
    if (!nodes.some((n) => n.type === 'generator.image')) return;
    autogenDone.current = true;
    // Consume the parameter: a reload or a shared link must not re-charge.
    params.delete('autogen');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
    const t = setTimeout(() => { handleGenerate(); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [wf, nodes]);

  const pollVideo = useCallback((gid) => {
    let tries = 0;
    const tick = async () => {
      if (cancelRef.current) return;
      tries += 1;
      try {
        const g = await api.getGeneration(gid);
        if (g.status === 'succeeded') {
          setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
          setVideoBusy(false);
          setVideoSource(null);
          const c = await api.getCredits(); updateCredits(c.balance);
          if (g.mocked) toast({ title: 'Mock video created', description: 'Add your FAL_KEY to render real motion with Veo 3.1.' });
          else toast({ title: 'UGC video ready', description: 'Your clip is in the results strip.' });
          return;
        }
        if (g.status === 'failed') {
          setVideoBusy(false);
          toast({ title: 'Video failed', description: g.error_message || 'Provider error. Credits refunded.' });
          setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
          const c = await api.getCredits(); updateCredits(c.balance);
          return;
        }
      } catch (e) { /* keep polling */ }
      if (tries < 120) setTimeout(tick, 3000);
      else setVideoBusy(false);
    };
    setTimeout(tick, 2000);
  }, [updateCredits]);

  const runVideo = async ({ model, duration, aspect, prompt, revealImageDataUrl, segments, language, voice, revealMode }) => {
    if (!videoSource) return;
    cancelRef.current = false;
    setVideoBusy(true);
    try {
      const res = await api.generateVideo({ generationId: videoSource.id, workflowId, model, duration, aspect, prompt, revealImageDataUrl, segments, language, voice, revealMode });
      // optimistic placeholder in strip
      setGenerations((prev) => [{ id: res.generationId, kind: 'video', status: 'queued', url: videoSource.url }, ...prev]);
      pollVideo(res.generationId);
    } catch (e) {
      setVideoBusy(false);
      if (e.status === 402) toast({ title: 'Not enough credits', description: `Video needs ${e.detail?.needed} credits.` });
      else toast({ title: 'Cannot create video', description: e.message });
    }
  };

  // Cold worker on a fresh model can take a good while to even start, so this
  // is far more patient than pollVideo -- giving up early here just means
  // telling someone their clip failed while it is still quietly finishing.
  const pollAvatarVideo = useCallback((gid) => {
    let tries = 0;
    const tick = async () => {
      if (cancelRef.current) return;
      tries += 1;
      try {
        const g = await api.getGeneration(gid);
        if (g.progress_total) {
          setAvatarProgress({ step: g.progress_step, total: g.progress_total, stage: g.progress_stage });
        }
        if (g.status === 'succeeded') {
          setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
          setAvatarBusy(false);
          setAvatarOpen(false);
          setAvatarProgress(null);
          const c = await api.getCredits(); updateCredits(c.balance);
          toast({ title: 'Avatar video ready', description: 'Your clip is in the results strip.' });
          return;
        }
        if (g.status === 'failed') {
          setAvatarBusy(false);
          setAvatarProgress(null);
          toast({ title: 'Avatar video failed', description: g.error_message || 'Provider error. Credits refunded.' });
          setGenerations((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
          const c = await api.getCredits(); updateCredits(c.balance);
          return;
        }
      } catch (e) { /* keep polling */ }
      if (tries < 300) setTimeout(tick, 3000);
      else {
        setAvatarBusy(false);
        setAvatarProgress(null);
        toast({ title: 'Still working on the server',
                description: 'Taking longer than expected -- check Creations shortly, it may still finish.' });
      }
    };
    setTimeout(tick, 2000);
  }, [updateCredits]);

  const runAvatarVideo = async ({ prompt, audio, script, image, numSegments, resolution }) => {
    cancelRef.current = false;
    setAvatarBusy(true);
    setAvatarProgress(null);
    try {
      const res = await api.generateAvatarVideo({ workflowId, prompt, audio, script, image, numSegments, resolution });
      setGenerations((prev) => [{ id: res.generationId, kind: 'avatar_video', status: 'queued', url: image || null }, ...prev]);
      pollAvatarVideo(res.generationId);
    } catch (e) {
      setAvatarBusy(false);
      if (e.status === 402) toast({ title: 'Not enough credits', description: `Avatar video needs ${e.detail?.needed} credits.` });
      else toast({ title: 'Cannot create avatar video', description: e.message });
    }
  };

  const addUnboxingStarter = async () => {
    const styleTpl = templates.find((t) => t.slug === 'studio-seamless') || templates[0];
    const box = { id: nextNodeId('concept'), type: 'asset.product', position: { x: 40, y: 40 }, data: { kind: 'product', name: 'an open branded shoebox with tissue paper' } };
    const item = { id: nextNodeId('concept'), type: 'asset.product', position: { x: 40, y: 240 }, data: { kind: 'product', name: 'a white leather sneaker' } };
    const combine = { id: nextNodeId('combine'), type: 'combine.scene', position: { x: 320, y: 130 }, data: { kind: 'combine', relation: 'the sneaker sitting inside the open shoebox with tissue paper, the sneaker clearly visible, top-down unboxing composition' } };
    const style = { id: nextNodeId('style'), type: 'asset.style', position: { x: 320, y: 320 }, data: { kind: 'style', templateSlug: styleTpl?.slug, name: styleTpl?.name, accent: styleTpl?.accent, intensity: 0.8 } };
    const img = { id: nextNodeId('gen'), type: 'generator.image', position: { x: 620, y: 170 }, data: { kind: 'generator', quality: 'premium', status: 'idle' } };
    const vid = { id: nextNodeId('vid'), type: 'generator.video', position: { x: 900, y: 150 }, data: { kind: 'video', model: 'fast', duration: 8, aspect: '9:16', status: 'idle', prompt: 'hands reach into the already-open box and lift the sneaker out, presenting it clearly to camera, handheld UGC unboxing style, natural motion' } };
    const graph = {
      version: 1,
      nodes: [box, item, combine, style, img, vid],
      edges: [
        { id: nextNodeId('e'), source: box.id, target: combine.id, animated: true },
        { id: nextNodeId('e'), source: item.id, target: combine.id, animated: true },
        { id: nextNodeId('e'), source: combine.id, target: img.id, animated: true },
        { id: nextNodeId('e'), source: style.id, target: img.id, animated: true },
        { id: nextNodeId('e'), source: img.id, target: vid.id, animated: true },
      ],
    };
    setGraph(graph);
    setSelected(img.id);
    try { await api.updateWorkflow(workflowId, { graph_json: graph }); markClean(); } catch (e) { /* keep dirty */ }
  };

  const addWebsiteReviewStarter = async () => {
    const styleTpl = templates.find((t) => t.slug === 'editorial-minimal') || templates[0];
    const web = { id: nextNodeId('web'), type: 'asset.website', position: { x: 40, y: 110 }, data: { kind: 'website', url: '', sceneMode: 'influencer' } };
    const style = { id: nextNodeId('style'), type: 'asset.style', position: { x: 40, y: 340 }, data: { kind: 'style', templateSlug: styleTpl?.slug, name: styleTpl?.name, accent: styleTpl?.accent, intensity: 0.7 } };
    const img = { id: nextNodeId('gen'), type: 'generator.image', position: { x: 400, y: 180 }, data: { kind: 'generator', quality: 'premium', status: 'idle' } };
    const vid = { id: nextNodeId('vid'), type: 'generator.video', position: { x: 720, y: 160 }, data: { kind: 'video', model: 'fast', duration: 8, aspect: '9:16', status: 'idle', prompt: 'the presenter points at the laptop screen showing the website and speaks enthusiastically about it, casual UGC talking-head review, natural gestures' } };
    const graph = {
      version: 1,
      nodes: [web, style, img, vid],
      edges: [
        { id: nextNodeId('e'), source: web.id, target: img.id, animated: true },
        { id: nextNodeId('e'), source: style.id, target: img.id, animated: true },
        { id: nextNodeId('e'), source: img.id, target: vid.id, animated: true },
      ],
    };
    setGraph(graph);
    setSelected(web.id);
    try { await api.updateWorkflow(workflowId, { graph_json: graph }); markClean(); } catch (e) { /* keep dirty */ }
  };

  const addTryOnStarter = async () => {
    const styleTpl = templates.find((t) => t.slug === 'editorial-minimal') || templates[0];
    const garment = { id: nextNodeId('concept'), type: 'asset.product', position: { x: 40, y: 40 }, data: { kind: 'product', name: 'the uploaded garment (drop your product photo here)' } };
    const model = { id: nextNodeId('concept'), type: 'asset.model', position: { x: 40, y: 240 }, data: { kind: 'model', name: 'a fashion model' } };
    const combine = { id: nextNodeId('combine'), type: 'combine.scene', position: { x: 320, y: 130 }, data: { kind: 'combine', relation: 'the model wearing the garment, the garment clearly visible and well-fitted, full-body fashion editorial shot' } };
    const style = { id: nextNodeId('style'), type: 'asset.style', position: { x: 320, y: 320 }, data: { kind: 'style', templateSlug: styleTpl?.slug, name: styleTpl?.name, accent: styleTpl?.accent, intensity: 0.75 } };
    const img = { id: nextNodeId('gen'), type: 'generator.image', position: { x: 620, y: 170 }, data: { kind: 'generator', quality: 'premium', status: 'idle' } };
    const vid = { id: nextNodeId('vid'), type: 'generator.video', position: { x: 900, y: 150 }, data: { kind: 'video', model: 'fast', duration: 8, aspect: '9:16', status: 'idle', prompt: 'the model turns to show the outfit from a few angles, mirror-selfie try-on UGC vibe, natural relaxed movement' } };
    const graph = {
      version: 1,
      nodes: [garment, model, combine, style, img, vid],
      edges: [
        { id: nextNodeId('e'), source: garment.id, target: combine.id, animated: true },
        { id: nextNodeId('e'), source: model.id, target: combine.id, animated: true },
        { id: nextNodeId('e'), source: combine.id, target: img.id, animated: true },
        { id: nextNodeId('e'), source: style.id, target: img.id, animated: true },
        { id: nextNodeId('e'), source: img.id, target: vid.id, animated: true },
      ],
    };
    setGraph(graph);
    setSelected(garment.id);
    try { await api.updateWorkflow(workflowId, { graph_json: graph }); markClean(); } catch (e) { /* keep dirty */ }
  };

  const addStarter = async (preset) => {
    const styleTpl = templates.find((t) => t.slug === preset.styleSlug) || templates[0];
    const sub = { id: nextNodeId('concept'), type: preset.subjectType, position: { x: 60, y: 90 }, data: { ...preset.subjectData } };
    const sty = { id: nextNodeId('style'), type: 'asset.style', position: { x: 60, y: 320 }, data: { kind: 'style', templateSlug: styleTpl?.slug, name: styleTpl?.name, accent: styleTpl?.accent, intensity: 0.8 } };
    const gen = { id: nextNodeId('gen'), type: 'generator.image', position: { x: 440, y: 190 }, data: { kind: 'generator', quality: 'premium', status: 'idle' } };
    const graph = {
      version: 1,
      nodes: [sub, sty, gen],
      edges: [
        { id: nextNodeId('e'), source: sub.id, target: gen.id, animated: true },
        { id: nextNodeId('e'), source: sty.id, target: gen.id, animated: true },
      ],
    };
    setGraph(graph);
    setSelected(gen.id);
    try { await api.updateWorkflow(workflowId, { graph_json: graph }); markClean(); } catch (e) { /* keep dirty */ }
  };

  // Templates named after what someone is actually selling, not after a lighting
  // style. Each carries its category, so the physical rule for that kind of
  // product is attached from the first render instead of waiting for an upload.
  // Kept in local state so typing stays responsive; the rename is sent when the
  // field loses focus rather than on every keystroke.
  const [wfName, setWfName] = useState('');
  useEffect(() => { setWfName(wf?.name || ''); }, [wf?.name]);

  const saveName = async () => {
    const next = (wfName || '').trim();
    if (!next || next === (wf?.name || '')) { setWfName(wf?.name || ''); return; }
    try {
      await api.updateWorkflow(workflowId, { name: next });
      setWf((prev) => (prev ? { ...prev, name: next } : prev));
      toast({ title: 'Session renamed', description: next });
    } catch (e) {
      setWfName(wf?.name || '');
      toast({ title: 'Could not rename', description: e.message });
    }
  };

  const STARTERS = [
    { label: 'Skincare review', subjectType: 'asset.product', styleSlug: 'clean-ecommerce', subjectData: { kind: 'product', category: 'skincare', name: 'a face moisturiser in a branded jar' } },
    { label: 'Book review', subjectType: 'asset.product', styleSlug: 'lifestyle-in-situ', subjectData: { kind: 'product', category: 'book', name: 'a paperback novel held in one hand' } },
    { label: 'Tech unboxing', subjectType: 'asset.product', styleSlug: 'studio-seamless', subjectData: { kind: 'product', category: 'electronics', name: 'a small consumer electronics device beside its open box' } },
    { label: 'Fashion try-on', subjectType: 'asset.model', styleSlug: 'editorial-minimal', subjectData: { kind: 'model', category: 'apparel', name: 'a model wearing the garment, full length' } },
    { label: 'Supplement', subjectType: 'asset.product', styleSlug: 'clean-ecommerce', subjectData: { kind: 'product', category: 'supplement', name: 'a supplement bottle with capsules beside it' } },
    { label: 'Travel promo', subjectType: 'asset.environment', styleSlug: 'lifestyle-in-situ', subjectData: { kind: 'environment', category: 'travel', name: 'a sunlit clifftop terrace overlooking the sea' } },
    { label: 'Software review', subjectType: 'asset.product', styleSlug: 'clean-ecommerce', subjectData: { kind: 'product', category: 'software', name: 'a laptop showing the product dashboard on screen' } },
    { label: 'Food or drink', subjectType: 'asset.product', styleSlug: 'lifestyle-in-situ', subjectData: { kind: 'product', category: 'food', name: 'the product plated and ready to eat' } },
  ];

  return (
    <div className="flex h-screen flex-col bg-[#0E0E10] text-white">
      {mode === 'simple' ? (
        <>
          <SimpleMode
            wf={wf}
            assets={assets}
            influencers={influencers}
            onAssetUploaded={reloadAssets}
            generations={generations}
            generating={generating}
            genCost={genCost}
            compiled={compiled}
            onGenerate={handleGenerate}
            onStop={handleStop}
            onOpenCanvas={() => switchMode('canvas')}
            onMakeVideo={videoOn ? ((g) => setVideoSource(g)) : null}
            saving={saving}
            user={user}
          />
          {sessions && <PhotoRecipes onClose={() => setSessions(false)} />}
          {videoOn && <VideoDialog source={videoSource} seed={videoSeed} onClose={() => !videoBusy && setVideoSource(null)} onGenerate={runVideo} busy={videoBusy} />}
          <AvatarVideoDialog open={avatarOpen} onClose={() => !avatarBusy && setAvatarOpen(false)} onGenerate={runAvatarVideo} busy={avatarBusy} isAdmin={user?.role === 'admin'} progress={avatarProgress} />
        </>
      ) : (
      <>
      {/* Top bar */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-[#2A2A30] px-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/app')} className="flex h-8 w-8 items-center justify-center rounded-md text-white/60 hover:bg-white/5">
            <ArrowLeft size={16} />
          </button>
          {/* The name was fixed text, so a session kept "Untitled session"
              for good. The endpoint already accepted a rename; only this did not
              offer it. Width follows the text so it does not sit in a box. */}
          <input
            value={wfName}
            onChange={(e) => setWfName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            data-testid="workflow-name"
            placeholder="Untitled session"
            size={Math.max(8, Math.min(40, (wfName || '').length + 1))}
            className="font-serif-display truncate rounded-md border border-transparent bg-transparent px-1.5 text-lg text-white hover:border-[#2A2A30] focus:border-white/30 focus:bg-[#141417] focus:outline-none"
          />
          <span className="flex items-center gap-1 text-xs text-white/40">
            {saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : <><Check size={12} className="text-node-environment" /> Saved</>}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Vuelta al modo Simple, el espejo de 'Open canvas'. Oculto en los
              grafos sin generador de imagen (UGC de video): alli Simple no
              tiene nada que conducir. */}
          {nodes.some((n) => n.type === 'generator.image')
            && !nodes.some((n) => n.type === 'generator.video' || n.type === 'generator.avatar_video') && (
          <button
            onClick={() => switchMode('simple')}
            data-testid="studio-simple-view"
            title="Back to the simple one-column view"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[#2A2A30] px-4 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
          >
            <LayoutList size={15} /> Simple view
          </button>
          )}
          {/* Starting from a recipe used to mean going back to the dashboard.
              It still makes its own workflow, because a session is a session,
              but it can be asked for from inside one. */}
          <button
            onClick={() => setSessions(true)}
            data-testid="studio-sessions-open"
            title="Ready made photo sessions"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[#2A2A30] px-4 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
          >
            <Camera size={15} /> Sessions
          </button>
          <button
            onClick={() => setAvatarOpen(true)}
            data-testid="studio-avatar-open"
            title="Audio-driven talking avatar clip"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[#2A2A30] px-4 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
          >
            <Mic size={15} /> Avatar
          </button>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#2A2A30] bg-[#141417] px-3 py-1.5 text-xs text-white/80">
            <Coins size={13} className="text-node-product" /> {user?.role === 'admin' ? '∞' : (user?.credits_balance ?? 0)} credits
          </span>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-5 text-sm font-medium text-black transition-transform hover:scale-[1.02] disabled:opacity-50"
            data-testid="generate-btn"
          >
            {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            {/* What it costs, where it is decided. A session of twelve at the
                premium tier is a hundred and twenty credits, and nobody should
                find that out afterwards. */}
            {generating ? 'Generating…' : (genCost ? `Generate · ${genCost} credits` : 'Generate')}
          </button>
          {generating && (
            <button
              onClick={handleStop}
              data-testid="stop-btn"
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-red-500/50 bg-red-500/10 px-4 text-sm font-medium text-red-300 transition-colors hover:bg-red-500/20"
            >
              <Square size={13} className="fill-current" /> Stop
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1">
        <LeftPanel assets={assets} templates={templates} influencers={influencers} onAssetUploaded={reloadAssets} onAddNode={addNodeAtCenter} />
        <div className="relative flex min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1" ref={wrapper} onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={(_, n) => setSelected(n.id)}
              onPaneClick={() => setSelected(null)}
              fitView
              proOptions={{ hideAttribution: true }}
              defaultEdgeOptions={{ style: { stroke: '#3A3A42', strokeWidth: 1.5 } }}
            >
              <Background color="#22222a" gap={22} size={1} />
              <Controls className="!border-[#2A2A30] !bg-[#141417] [&>button]:!border-[#2A2A30] [&>button]:!bg-[#141417] [&>button]:!fill-white" />
              <MiniMap pannable zoomable nodeColor="#2A2A30" maskColor="rgba(14,14,16,0.7)" style={{ background: '#141417', border: '1px solid #2A2A30' }} />
            </ReactFlow>
            {nodes.length === 0 && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="pointer-events-auto max-w-md text-center">
                  <p className="font-serif-display text-2xl text-white/80">Start with a template</p>
                  <p className="mt-2 text-sm text-white/45">Or drag a subject, a style and the generator from the left, then wire them up.</p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {STARTERS.map((s) => (
                      <button
                        key={s.label}
                        onClick={() => addStarter(s)}
                        className="rounded-full border border-[#2A2A30] bg-[#141417] px-4 py-2 text-sm text-white/85 transition-colors hover:border-white/40 hover:text-white"
                      >
                        {s.label}
                      </button>
                    ))}
                    <button
                      onClick={addUnboxingStarter}
                      className="rounded-full border border-node-model/50 bg-node-model/10 px-4 py-2 text-sm font-medium text-node-model transition-colors hover:bg-node-model/20"
                    >
                      UGC Unboxing
                    </button>
                    <button
                      onClick={addWebsiteReviewStarter}
                      data-testid="starter-website-review"
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#38BDF8]/50 bg-[#38BDF8]/10 px-4 py-2 text-sm font-medium text-[#38BDF8] transition-colors hover:bg-[#38BDF8]/20"
                    >
                      Website Review
                    </button>
                    <button
                      onClick={addTryOnStarter}
                      data-testid="starter-try-on"
                      className="inline-flex items-center gap-1.5 rounded-full border border-node-style/50 bg-node-style/10 px-4 py-2 text-sm font-medium text-node-style transition-colors hover:bg-node-style/20"
                    >
                      Model Try-On
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <ResultsStrip generations={generations} onMakeVideo={videoOn ? ((g) => setVideoSource(g)) : null} />
        </div>
        <RightPanel node={selectedNode} onChange={updateNodeData} onRemove={removeNode} compiled={compiled} compiling={compiling} onMakeVideo={videoOn ? ((g) => setVideoSource(g)) : null} influencer={selectedInfluencer} generations={generations} onGenerate={handleGenerate} onStop={handleStop} generating={generating} genCost={genCost} />
      </div>
      {sessions && <PhotoRecipes onClose={() => setSessions(false)} />}
      {videoOn && <VideoDialog source={videoSource} seed={videoSeed} onClose={() => !videoBusy && setVideoSource(null)} onGenerate={runVideo} busy={videoBusy} />}
      <AvatarVideoDialog open={avatarOpen} onClose={() => !avatarBusy && setAvatarOpen(false)} onGenerate={runAvatarVideo} busy={avatarBusy} isAdmin={user?.role === 'admin'} progress={avatarProgress} />
      </>
      )}
    </div>
  );
}

export default function Studio() {
  const { workflowId } = useParams();
  return (
    <ReactFlowProvider>
      <Canvas workflowId={workflowId} />
    </ReactFlowProvider>
  );
}
